import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapPin, Mail, Phone, TrendingUp, TrendingDown, Cookie } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";

export default function Footer() {
  const s = useSettings();
  const cookieConsent = useCookieConsent();
  const [stock, setStock] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.get("/stock/abg")
        .then((r) => { if (mounted) setStock(r.data); })
        .catch(() => {});
    };
    load();
    // Refresh every 60 s — backend caches upstream calls, so this is cheap.
    const id = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const price = stock?.price != null ? Number(stock.price).toFixed(2) : null;
  const pct = stock?.percent_change != null ? Number(stock.percent_change) : null;
  const positive = (pct ?? 0) >= 0;
  const TrendIcon = positive ? TrendingUp : TrendingDown;
  const isLive = stock && stock.source && stock.source !== "fallback";
  return (
    <footer data-testid="site-footer" className="bg-emerald-darker text-bone relative overflow-hidden">
      <div className="grain" />
      <div className="container-x py-12 sm:py-16 lg:py-20 relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-8 sm:gap-10 md:gap-12">
          <div className="sm:col-span-2 md:col-span-4">
            <div className="flex items-center gap-3 mb-5 sm:mb-6">
              <Logo size={48} variant="dark" />
              <div>
                <div className="font-serif text-lg sm:text-xl">{s.company_name}</div>
                <div className="overline text-[0.6rem] text-gold">{s.stock_symbol}</div>
              </div>
            </div>
            <p className="text-sm text-bone/70 leading-relaxed max-w-sm">{s.footer_description}</p>
            <div
              data-testid="footer-stock-ticker"
              className="mt-5 sm:mt-6 flex items-center gap-2 text-sm text-bone/60"
            >
              <TrendIcon className={`w-4 h-4 ${positive ? "text-neon" : "text-red-400"}`} />
              <span className="font-mono">
                {s.stock_symbol}
                {" · "}
                {price ? `₱${price}` : <span className="text-bone/30">—</span>}
                {" · "}
                <span className={positive ? "text-neon" : "text-red-400"}>
                  {pct != null ? `${positive ? "+" : ""}${pct.toFixed(2)}%` : "—"}
                </span>
              </span>
              {isLive && (
                <span
                  title={`Live · source: ${stock.source}`}
                  className="ml-1 inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider text-neon/80"
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon opacity-70" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-neon" />
                  </span>
                  Live
                </span>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="overline text-gold mb-4 sm:mb-5">Company</h4>
            <ul className="space-y-2.5 sm:space-y-3 text-sm text-bone/80">
              <li><Link to="/p/the-asiabest-difference" className="hover:text-gold transition-colors">About</Link></li>
              <li><Link to="/news" className="hover:text-gold transition-colors">News</Link></li>
              <li><Link to="/disclosures" className="hover:text-gold transition-colors">Disclosures</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline text-gold mb-4 sm:mb-5">Investor Relations</h4>
            <ul className="space-y-2.5 sm:space-y-3 text-sm text-bone/80">
              <li><Link to="/disclosures" className="hover:text-gold transition-colors">Public Disclosures</Link></li>
              <li><Link to="/disclosures" className="hover:text-gold transition-colors">Financial Reports</Link></li>
              <li><Link to="/news" className="hover:text-gold transition-colors">Press Releases</Link></li>
              <li><Link to="/admin/login" data-testid="admin-login-link" className="hover:text-gold transition-colors">Administrator Login</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline text-gold mb-4 sm:mb-5">Get in Touch</h4>
            <ul className="space-y-3 sm:space-y-4 text-sm text-bone/80">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                <span>{s.address_short}</span>
              </li>
              <li className="flex gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                <a href={`mailto:${s.email_ir}`} className="hover:text-gold transition-colors break-all">{s.email_ir}</a>
              </li>
              <li className="flex gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                <span>{s.phone_main}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 sm:mt-14 lg:mt-16 pt-6 sm:pt-8 border-t border-bone/10 flex flex-col md:flex-row justify-between gap-3 sm:gap-4 text-xs text-bone/50">
          <p>© {new Date().getFullYear()} {s.copyright_holder}. All rights reserved. A publicly listed Philippine corporation.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 sm:items-center flex-wrap">
            {/* <Link to="/p/privacy-policy" className="hover:text-gold transition-colors">Privacy Policy</Link>
            <Link to="/p/cookie-policy" className="hover:text-gold transition-colors">Cookie Policy</Link> */}
            <button
              type="button"
              onClick={cookieConsent.openPreferences}
              data-testid="footer-cookie-preferences"
              className="inline-flex items-center gap-1.5 hover:text-gold transition-colors text-left"
            >
              <Cookie className="w-3.5 h-3.5" /> Cookie Preferences
            </button>
            <span>{s.sec_registration}</span>
            <span>PSE Listed</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
